import { useRef } from 'react'
import DOMPurify from 'dompurify'
import type { BrandingData } from '../api'

interface BrandingSettingsProps {
  brandingData: BrandingData | null
  brandingLoading: boolean
  brandingSaving: boolean
  brandingSuccess: boolean
  brandingError: string | null
  brandingPrimaryColor: string
  brandingAccentColor: string
  brandingDisplayName: string
  brandingFooterText: string
  brandingEmailHeader: string
  brandingHidePoweredBy: boolean
  brandingLetterheadEnabled: boolean
  brandingLetterheadHtml: string
  brandingLogoUploading: boolean
  userName: string
  onPrimaryColorChange: (val: string) => void
  onAccentColorChange: (val: string) => void
  onDisplayNameChange: (val: string) => void
  onFooterTextChange: (val: string) => void
  onEmailHeaderChange: (val: string) => void
  onHidePoweredByToggle: () => void
  onLetterheadEnabledToggle: () => void
  onLetterheadHtmlChange: (val: string) => void
  onResetColors: () => void
  onLogoUpload: (file: File) => void
  onLogoDelete: () => void
  onSave: () => void
  onClose: () => void
}

export default function BrandingSettings({
  brandingData,
  brandingLoading,
  brandingSaving,
  brandingSuccess,
  brandingError,
  brandingPrimaryColor,
  brandingAccentColor,
  brandingDisplayName,
  brandingFooterText,
  brandingEmailHeader,
  brandingHidePoweredBy,
  brandingLetterheadEnabled,
  brandingLetterheadHtml,
  brandingLogoUploading,
  userName,
  onPrimaryColorChange,
  onAccentColorChange,
  onDisplayNameChange,
  onFooterTextChange,
  onEmailHeaderChange,
  onHidePoweredByToggle,
  onLetterheadEnabledToggle,
  onLetterheadHtmlChange,
  onResetColors,
  onLogoUpload,
  onLogoDelete,
  onSave,
  onClose,
}: BrandingSettingsProps) {
  const brandingFileInputRef = useRef<HTMLInputElement>(null)

  return (
    <div className="p-6 space-y-5 max-h-[520px] overflow-y-auto">
      {brandingError && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600 font-medium">{brandingError}</div>
      )}
      {brandingSuccess && (
        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-sm text-emerald-600 font-medium">Branding settings saved successfully!</div>
      )}

      {brandingLoading ? (
        <div className="space-y-3 py-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="p-4 bg-slate-50 rounded-xl">
              <div className="skeleton h-4 w-32 mb-2" />
              <div className="skeleton h-8 w-full" />
            </div>
          ))}
        </div>
      ) : (
        <>
          {/* Enterprise badge */}
          <div className="flex items-center gap-2 mb-1">
            <span className="inline-flex items-center text-[10px] font-bold px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 border border-violet-200">ENTERPRISE</span>
            <span className="text-xs text-slate-400">Custom white-label branding for your portal</span>
          </div>

          {/* Logo Upload */}
          <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
            <label className="block text-sm font-semibold text-slate-700 mb-2">Practice Logo</label>
            {brandingData?.practice_logo_url ? (
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 bg-white rounded-lg border border-slate-200 flex items-center justify-center p-2 overflow-hidden">
                  <img src={brandingData.practice_logo_url} alt="Practice logo" className="max-w-full max-h-full object-contain" />
                </div>
                <div className="flex flex-col gap-2">
                  <button onClick={() => brandingFileInputRef.current?.click()} disabled={brandingLogoUploading}
                    className="px-3 py-1.5 text-xs font-semibold text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors disabled:opacity-50">
                    {brandingLogoUploading ? 'Uploading...' : 'Replace Logo'}
                  </button>
                  <button onClick={onLogoDelete} disabled={brandingLogoUploading}
                    className="px-3 py-1.5 text-xs font-semibold text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50">
                    Remove Logo
                  </button>
                </div>
              </div>
            ) : (
              <div
                onClick={() => brandingFileInputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation() }}
                onDrop={(e) => { e.preventDefault(); e.stopPropagation(); const f = e.dataTransfer.files[0]; if (f) onLogoUpload(f) }}
                className="border-2 border-dashed border-slate-300 rounded-xl p-6 text-center cursor-pointer hover:border-violet-400 hover:bg-violet-50/50 transition-colors"
              >
                <svg className="w-8 h-8 text-slate-300 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                </svg>
                <p className="text-sm font-semibold text-slate-500">{brandingLogoUploading ? 'Uploading...' : 'Click or drag to upload logo'}</p>
                <p className="text-xs text-slate-400 mt-1">PNG, JPG, or SVG. Max 2MB.</p>
              </div>
            )}
            <input ref={brandingFileInputRef} type="file" accept=".png,.jpg,.jpeg,.svg" className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) onLogoUpload(f); e.target.value = '' }} />
          </div>

          {/* Color Pickers */}
          <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-semibold text-slate-700">Brand Colors</label>
              <button onClick={onResetColors} className="text-xs text-slate-400 hover:text-slate-600 font-medium transition-colors">
                Reset to defaults
              </button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1.5">Primary Color</label>
                <div className="flex items-center gap-2">
                  <input type="color" value={brandingPrimaryColor} onChange={e => onPrimaryColorChange(e.target.value)}
                    className="w-10 h-10 rounded-lg border border-slate-200 cursor-pointer p-0.5" />
                  <input type="text" value={brandingPrimaryColor} onChange={e => onPrimaryColorChange(e.target.value)}
                    className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1.5">Accent Color</label>
                <div className="flex items-center gap-2">
                  <input type="color" value={brandingAccentColor} onChange={e => onAccentColorChange(e.target.value)}
                    className="w-10 h-10 rounded-lg border border-slate-200 cursor-pointer p-0.5" />
                  <input type="text" value={brandingAccentColor} onChange={e => onAccentColorChange(e.target.value)}
                    className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent" />
                </div>
              </div>
            </div>
            {/* Live preview swatches */}
            <div className="mt-3 flex items-center gap-2">
              <span className="text-xs text-slate-400">Preview:</span>
              <div className="flex gap-1.5">
                <div className="w-8 h-5 rounded" style={{ background: brandingPrimaryColor }} title="Primary" />
                <div className="w-8 h-5 rounded" style={{ background: brandingAccentColor }} title="Accent" />
                <div className="w-20 h-5 rounded" style={{ background: `linear-gradient(135deg, ${brandingPrimaryColor}, ${brandingAccentColor})` }} title="Gradient" />
              </div>
            </div>
          </div>

          {/* Practice Display Name */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Practice Display Name</label>
            <input type="text" value={brandingDisplayName} onChange={e => onDisplayNameChange(e.target.value)}
              placeholder={userName || 'Your Practice Name'}
              className="w-full border border-slate-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent" />
            <p className="text-xs text-slate-400 mt-1">Overrides the practice name in the app header and generated letters.</p>
          </div>

          {/* Custom Footer Text */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Custom Footer Text</label>
            <textarea value={brandingFooterText} onChange={e => onFooterTextChange(e.target.value)}
              placeholder="e.g., Confidential — For insurance review purposes only."
              rows={2}
              className="w-full border border-slate-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-none" />
            <p className="text-xs text-slate-400 mt-1">Appears at the bottom of generated letters.</p>
          </div>

          {/* Custom Email Header */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Custom Email Header Text</label>
            <input type="text" value={brandingEmailHeader} onChange={e => onEmailHeaderChange(e.target.value)}
              placeholder="e.g., Your Letter of Medical Necessity is ready"
              className="w-full border border-slate-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent" />
            <p className="text-xs text-slate-400 mt-1">Custom subtitle text in email notifications.</p>
          </div>

          {/* Letterhead Toggle + HTML */}
          <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-semibold text-slate-700">Custom Letterhead</label>
              <button onClick={onLetterheadEnabledToggle}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${brandingLetterheadEnabled ? 'bg-violet-600' : 'bg-slate-300'}`}>
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${brandingLetterheadEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>
            <p className="text-xs text-slate-400 mb-3">Use custom letterhead HTML on generated letters (practice name, address, phone, logo).</p>
            {brandingLetterheadEnabled && (
              <textarea value={brandingLetterheadHtml} onChange={e => onLetterheadHtmlChange(e.target.value)}
                placeholder={'<div style="text-align:center;">\n  <strong>Your Practice Name</strong><br/>\n  123 Medical Dr, Suite 100<br/>\n  City, State 12345 | (555) 123-4567\n</div>'}
                rows={5}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-none" />
            )}
          </div>

          {/* Hide RxCheckUp branding toggle */}
          <div className="flex items-center justify-between bg-slate-50 rounded-xl p-4 border border-slate-200">
            <div>
              <label className="text-sm font-semibold text-slate-700">Hide "Powered by RxCheckUp"</label>
              <p className="text-xs text-slate-400 mt-0.5">Remove RxCheckUp branding from letters and emails.</p>
            </div>
            <button onClick={onHidePoweredByToggle}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${brandingHidePoweredBy ? 'bg-violet-600' : 'bg-slate-300'}`}>
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${brandingHidePoweredBy ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>

          {/* Live Preview Section */}
          <div className="border border-slate-200 rounded-xl overflow-hidden">
            <div className="bg-slate-100 px-4 py-2 border-b border-slate-200">
              <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">Live Preview</span>
            </div>
            {/* App Header Bar Preview */}
            <div className="px-4 pt-3">
              <span className="text-[10px] font-semibold text-slate-400 uppercase">App Header</span>
            </div>
            <div className="mx-4 mt-1 rounded-lg overflow-hidden" style={{ background: `linear-gradient(135deg, ${brandingPrimaryColor}, ${brandingPrimaryColor}dd)` }}>
              <div className="px-4 py-3 flex items-center gap-3">
                {brandingData?.practice_logo_url && (
                  <img src={brandingData.practice_logo_url} alt="Logo" className="h-7 object-contain" />
                )}
                <span className="text-white font-bold text-sm">{brandingDisplayName || userName || 'Your Practice'}</span>
              </div>
            </div>
            {/* Letter Header Preview */}
            <div className="px-4 pt-3">
              <span className="text-[10px] font-semibold text-slate-400 uppercase">Letter Header</span>
            </div>
            <div className="mx-4 mt-1 bg-white border border-slate-200 rounded-lg p-3">
              {brandingLetterheadEnabled && brandingLetterheadHtml && (
                <div className="mb-2 pb-2 border-b border-slate-100 text-xs text-slate-600" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(brandingLetterheadHtml) }} />
              )}
              <div className="flex items-center gap-2">
                {brandingData?.practice_logo_url && (
                  <img src={brandingData.practice_logo_url} alt="Logo" className="h-6 object-contain" />
                )}
                <div>
                  <p className="text-xs font-bold text-slate-800">{brandingDisplayName || userName || 'Your Practice'}</p>
                  <p className="text-[10px] text-slate-400">Letter of Medical Necessity</p>
                </div>
              </div>
              {brandingFooterText && (
                <p className="text-[10px] text-slate-400 mt-2 pt-2 border-t border-slate-100">{brandingFooterText}</p>
              )}
              {!brandingHidePoweredBy && (
                <p className="text-[10px] text-slate-300 mt-1">Powered by RxCheckUp</p>
              )}
            </div>
            {/* Email Header Preview */}
            <div className="px-4 pt-3">
              <span className="text-[10px] font-semibold text-slate-400 uppercase">Email Header</span>
            </div>
            <div className="mx-4 mt-1 mb-4 rounded-lg overflow-hidden">
              <div className="text-center py-4 px-3" style={{ background: `linear-gradient(135deg, ${brandingPrimaryColor}, ${brandingAccentColor})` }}>
                {brandingData?.practice_logo_url && (
                  <img src={brandingData.practice_logo_url} alt="Logo" className="h-6 object-contain mx-auto mb-1.5" />
                )}
                <p className="text-white font-bold text-sm">{brandingDisplayName || userName || 'Your Practice'}</p>
                <p className="text-white/70 text-[10px] mt-0.5">{brandingEmailHeader || 'Your Letter of Medical Necessity is ready'}</p>
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex gap-3 pt-1">
            <button onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-slate-300 text-slate-600 text-sm font-semibold rounded-xl hover:bg-slate-50 transition-colors">
              Close
            </button>
            <button onClick={onSave} disabled={brandingSaving}
              className="flex-1 px-4 py-2.5 bg-violet-600 text-white text-sm font-semibold rounded-xl hover:bg-violet-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              {brandingSaving ? 'Saving...' : 'Save Branding'}
            </button>
          </div>
        </>
      )}
    </div>
  )
}
